import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { AllocationField } from './AllocationField';
import { SlaBurndown } from './SlaBurndown';
import { SummaryBarProps } from './SummaryBar.props';
import { SummaryBarActions } from './SummaryBarActions';
import { TagsField } from './TagsField';
import { getServiceLocationName } from '../../../utils/service-location';
import { LogoWithFallback } from '../../atoms/LogoWithFallback';
import { PriorityDot } from '../../atoms/PriorityDot';
import { StatusBadge } from '../../atoms/StatusBadge';

const Divider: React.FC = () => (
  <Box sx={{ width: '1px', height: 24, bgcolor: 'v4.border', flexShrink: 0 }} />
);

export const SummaryBar: React.FC<SummaryBarProps> = ({
  service,
  logoPic,
  titleOverride,
  hideAllocation = false,
  allTags,
  onSaveTags,
  onStatusChange,
  onAllocate,
  onPartAction,
  onTask,
  onCancelFutile,
  onReopen,
  statusDisabledReason,
}) => {
  const statusName = service?.serviceStatus?.name ?? '';
  const priorityLabel = service?.priorityLabel ?? service?.priorityCode ?? '';
  const technicianName = service?.primaryAllocation?.node?.name ?? '';
  const locationName = getServiceLocationName(service);
  const contractName = service?.contract ?? '';
  const tags = service?.tags ?? [];

  const defaultTitle = `${service?.id ?? '—'}${locationName ? `: ${locationName}` : ''}`;
  const titleText = titleOverride || defaultTitle;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'v4.border',
        borderRadius: '8px',
        px: 2,
        py: 1,
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Left section — fills space, shrinks to make room for buttons */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          overflow: 'hidden',
        }}
      >
        {logoPic ? (
          <LogoWithFallback
            src={logoPic}
            code={service?.contractCode || service?.clientCode}
            label={contractName || 'Contract logo'}
            height={32}
            maxWidth={120}
          />
        ) : (
          <Typography
            sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {contractName || '—'}
          </Typography>
        )}

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          {titleText}
        </Typography>

        {/* Status, priority, allocation, tags, SLA — hidden during printing */}
        <Box sx={{ display: 'contents', '@media print': { display: 'none' } }}>
          <Divider />

          {statusName && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                '& > *': { fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.5 },
              }}
            >
              <StatusBadge status={statusName} />
            </Box>
          )}
          {priorityLabel && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                '& > *': { fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.5 },
              }}
            >
              <PriorityDot priority={priorityLabel} />
            </Box>
          )}

          {!hideAllocation && (
            <>
              <Divider />
              <AllocationField label="Technician" value={technicianName} />
            </>
          )}

          {(tags.length > 0 || onSaveTags) && (
            <>
              <Divider />
              <TagsField tags={tags} allTags={allTags ?? []} onSave={onSaveTags} />
            </>
          )}
          {/* SLA burndown bars */}
          {(service?.clientSLA || service?.opsSLA) && <Divider />}
          <SlaBurndown label="Client SLA" sla={service?.clientSLA} />
          <SlaBurndown label="Ops SLA" sla={service?.opsSLA} />
        </Box>
      </Box>

      {/* Right section — action buttons, right-aligned, never shrink; hidden during printing */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          flexShrink: 0,
          ml: 'auto',
          '@media print': { display: 'none' },
        }}
      >
        <SummaryBarActions
          onStatusChange={onStatusChange}
          onAllocate={onAllocate}
          onPartAction={onPartAction}
          onTask={onTask}
          onCancelFutile={onCancelFutile}
          onReopen={onReopen}
          statusDisabledReason={statusDisabledReason}
        />
      </Box>
    </Box>
  );
};
